import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CampaignClass, IFeatureMap } from '@feathr/blackbox';
import { useAccount } from '@feathr/extender/state';

import type { TLicenseFeatures } from './useLicense.types';
import { useMinPackageForFeature } from './useMinPackageForFeature';

/**
 * useMinPackageTooltip
 *
 * A custom React hook that returns a tooltip message if the current account's package does not meet the minimum required package for a given feature.
 *
 * This hook checks the user's active package against the minimum required package for the specified feature and category.
 * If the user does not have access, it returns a translated upgrade message. If the user already has access, it returns undefined.
 *
 * @param featureKey - The key of the feature to check access for.
 * @param category - The category of the feature within the IFeatureMap.
 * @returns A translated tooltip string prompting the user to upgrade, or undefined if the user has access.
 */
export function useMinPackageTooltip(
  featureKey: keyof TLicenseFeatures | CampaignClass,
  category?: keyof IFeatureMap,
): string | undefined {
  const { activePackage } = useAccount();
  const { t } = useTranslation();
  const minPackage = useMinPackageForFeature(activePackage, featureKey, category);

  return useMemo(() => {
    if (minPackage) {
      return t('Upgrade to {{packageName}} to access this feature.', {
        packageName: minPackage,
        feature: featureKey,
      });
    }
    return undefined;
  }, [minPackage, t, featureKey]);
}
